import type {
  Article,
  ArticleEditorView,
  ArticleRevision,
  ArticleRevisionSummary,
  ArticleSummary,
  ArticleUpsertInput,
} from "@care-by-ella/contracts";
import { API_BASE, apiFetch, tenantHeader, withCredentials } from "./api";
import { UnauthorizedError } from "./clinician-api";

// CAR-119/120 — clinician article (lesson/content) editor. Auth rides on the
// httpOnly session cookie like every other clinician request (see
// clinician-api.ts): `withCredentials` attaches it, `tenantHeader()` scopes the
// tenant, and state-changing requests carry the browser's Origin for the CSRF
// guard. No JWT in JS.

function commonHeaders(): Record<string, string> {
  return {
    "Content-Type": "application/json",
    ...tenantHeader(),
  };
}

/**
 * Surfaces the structured `{ error, messageKey }` envelope the API returns on
 * validation/conflict errors (e.g. `article_key_taken`, `validation_failed`)
 * so the editor can map the code to a human message. `code` is the machine
 * `error` string; `message` falls back to the status when the body isn't JSON.
 */
export class ArticleApiError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
  ) {
    super(message ?? code);
  }
}

async function jsonOrError<T>(res: Response): Promise<T> {
  if (res.status === 401 || res.status === 403) {
    throw new UnauthorizedError(`HTTP ${res.status}`);
  }
  if (!res.ok) {
    let code: string | null = null;
    try {
      const body = (await res.json()) as { error?: string; message?: string };
      if (body?.error) code = body.error;
    } catch {
      // non-JSON body — fall back to status code
    }
    throw new ArticleApiError(
      code ?? `http_${res.status}`,
      code ?? `HTTP ${res.status}`,
    );
  }
  return (await res.json()) as T;
}

export async function fetchArticles(filters?: {
  type?: string;
  status?: string;
}): Promise<ArticleSummary[]> {
  const params = new URLSearchParams();
  if (filters?.type) params.set("type", filters.type);
  if (filters?.status) params.set("status", filters.status);
  const qs = params.toString();
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/articles${qs ? `?${qs}` : ""}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

export async function fetchArticle(key: string): Promise<Article> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/articles/${encodeURIComponent(key)}`,
    { headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

export async function createArticle(
  input: ArticleUpsertInput,
): Promise<Article> {
  const res = await apiFetch(`${API_BASE}/employee-api/v1/articles`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify(input),
    ...withCredentials,
  });
  return jsonOrError(res);
}

export async function updateArticle(
  key: string,
  input: ArticleUpsertInput,
): Promise<Article> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/articles/${encodeURIComponent(key)}`,
    {
      method: "PUT",
      headers: commonHeaders(),
      body: JSON.stringify(input),
      ...withCredentials,
    },
  );
  return jsonOrError(res);
}

export async function publishArticle(key: string): Promise<Article> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/articles/${encodeURIComponent(key)}/publish`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

export async function unpublishArticle(key: string): Promise<Article> {
  const res = await apiFetch(
    `${API_BASE}/employee-api/v1/articles/${encodeURIComponent(key)}/unpublish`,
    { method: "POST", headers: commonHeaders(), ...withCredentials },
  );
  return jsonOrError(res);
}

// TES-207 — draft→publish editing, version history, rollback.

const editorBase = (key: string) =>
  `${API_BASE}/employee-api/v1/articles/${encodeURIComponent(key)}`;

// Live article + pending draft (or null) + count of published versions.
export async function fetchArticleEditor(
  key: string,
): Promise<ArticleEditorView> {
  const res = await apiFetch(`${editorBase(key)}/editor`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// Save/overwrite the pending edit buffer (does not touch live content).
export async function saveArticleDraft(
  key: string,
  input: ArticleUpsertInput,
): Promise<ArticleEditorView> {
  const res = await apiFetch(`${editorBase(key)}/draft`, {
    method: "PUT",
    headers: commonHeaders(),
    body: JSON.stringify(input),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// Drop the pending edit buffer.
export async function discardArticleDraft(
  key: string,
): Promise<ArticleEditorView> {
  const res = await apiFetch(`${editorBase(key)}/draft`, {
    method: "DELETE",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// Promote the pending draft live (version bump + revision snapshot).
export async function publishArticleDraft(key: string): Promise<Article> {
  const res = await apiFetch(`${editorBase(key)}/publish-draft`, {
    method: "POST",
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// Published-version history summaries (newest first).
export async function fetchArticleRevisions(
  key: string,
): Promise<ArticleRevisionSummary[]> {
  const res = await apiFetch(`${editorBase(key)}/revisions`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// A single published version's full content.
export async function fetchArticleRevision(
  key: string,
  version: number,
): Promise<ArticleRevision> {
  const res = await apiFetch(`${editorBase(key)}/revisions/${version}`, {
    headers: commonHeaders(),
    ...withCredentials,
  });
  return jsonOrError(res);
}

// Forward-only rollback: load an old version into the draft buffer for review.
export async function rollbackArticle(
  key: string,
  toVersion: number,
): Promise<ArticleEditorView> {
  const res = await apiFetch(`${editorBase(key)}/rollback`, {
    method: "POST",
    headers: commonHeaders(),
    body: JSON.stringify({ toVersion }),
    ...withCredentials,
  });
  return jsonOrError(res);
}
